import { createFileRoute, useNavigate } from '@tanstack/react-router';
import { useAuth0 } from '@auth0/auth0-react';
import { getLoggedUser } from '@/app/services/userService';
import { useAuth } from '@/app/utils/auth';
import { Spinner } from '@/app/ui/components/spinner';
import { useTranslations } from 'use-intl';
import { toastManager } from '@/app/ui/components/toast';
import * as React from 'react';
import * as Sentry from '@sentry/react';
import { z } from 'zod';
import { validator } from '@/app/validator';
import { LocaleContext } from '@/app/i18n/context';

export const Route = createFileRoute('/(auth)/_layout/auth0/callback')({
  component: RouteComponent,
  validateSearch: validator(
    z.object({
      code: z.string().optional(),
      state: z.string().optional(),
      error: z.string().optional(),
      error_description: z.string().optional(),
    }),
  ),
});

function RouteComponent() {
  const t = useTranslations('pages.login.auth0');
  const tGlobal = useTranslations();
  const { handleRedirectCallback, getAccessTokenSilently } = useAuth0();
  const { login, isLoading } = useAuth();
  const { setLocale } = React.use(LocaleContext);
  const navigate = useNavigate();
  const search = Route.useSearch();
  const [error, setError] = React.useState(false);
  const callbackHandled = React.useRef(false);

  React.useEffect(() => {
    const redirect = async (returnTo?: string) => {
      const token = await getAccessTokenSilently();
      const { data: loggedUser } = await getLoggedUser(token);

      if (loggedUser.systemLanguage) {
        setLocale(loggedUser.systemLanguage);
      }

      const currentOrg = loggedUser.organizations?.find(
        (org) => org.organizationId === loggedUser.organizationId,
      );
      if (!loggedUser.organizationId) {
        navigate({ to: '/add-organization' });
      } else if (!currentOrg?.isEnabled) {
        navigate({ to: '/choose-organization' });
      } else if (returnTo) {
        navigate({ to: returnTo });
      } else {
        navigate({ to: '/' });
      }
    };

    const handleCallback = async () => {
      if (callbackHandled.current) return;
      callbackHandled.current = true;

      try {
        const result = await handleRedirectCallback();
        const {
          returnTo: returnToUrl,
          activationSuccess,
          organizationName,
        } = result?.appState ?? {};

        navigate({ to: '.', search: {} });

        if (activationSuccess) {
          toastManager.add({
            type: 'success',
            title: tGlobal('pages.login.alerts.activatedSuccessfully'),
            timeout: Infinity,
          });
        }
        if (organizationName) {
          toastManager.add({
            type: 'success',
            title: tGlobal('pages.login.alerts.successfullyJoined'),
            description: tGlobal.rich('pages.acceptInvitation.alerts.youAreAMember', {
              organizationName,
              b: (chunks) => <strong>{chunks}</strong>,
            }),
            timeout: Infinity,
          });
        }

        await redirect(returnToUrl);
      } catch (e) {
        setError(true);
        Sentry.captureException(e);
      }
    };

    if (search.code && search.state) {
      void handleCallback();
    } else if (!isLoading && !callbackHandled.current) {
      if (search.error === 'access_denied' && search.error_description === 'email_not_verified') {
        navigate({ to: '/activate-account' });
      } else void redirect();
    }
  }, [
    handleRedirectCallback,
    getAccessTokenSilently,
    login,
    navigate,
    search,
    isLoading,
    tGlobal,
    setLocale,
  ]);

  if (error) {
    return (
      <div className="flex flex-col items-center justify-center min-h-screen w-full">
        <p className="text-destructive mb-4">{t('authenticationFailed')}</p>
        <button
          onClick={() => login()}
          className="text-sm text-primary underline hover:no-underline"
        >
          {t('goToLogin')}
        </button>
      </div>
    );
  }

  return (
    <div className="flex flex-col items-center justify-center min-h-screen w-full">
      <Spinner size="lg" />
      <p className="mt-4 text-muted">{t('loggingIn')}</p>
    </div>
  );
}
